import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Axios from 'axios';
import styled from 'styled-components';
import classNames from 'classnames';
import orderBy from 'lodash/orderBy';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isObject from 'lodash/isObject';
import every from 'lodash/every';
import { RowDivision, DivisionLabel, HelperText } from '../shared/styled-components';
import ModalDialog, { SaveButton, CloseButton } from '../../../../components/ModalDialog';
import { episodeManager } from '../../../../helpers/managers';
import { canUserAccessFeature } from '../../../../helpers/userinfoHelper';
import { ALERTS } from '../../../../helpers/texts';
import { ActionButton, HigherPlanTag } from '../../../../styles/styled';
import DrippingInput from './drippinginput';
import Loading from '../../../../components/Loading';

class Dripping extends Component {
  state = {
    show: false,
    updating: false,
    episodes: [],
    updatedEpisodes: {},
  };

  componentDidMount() {
    this.setEpisodes();
  }

  async setEpisodes() {
    const { episodes = {} } = this.props;
    const allowDraftEpisode = true;
    const nepisodes = await Promise.all(
      Object.keys(episodes).map(episodeId => {
        return new Promise(async resolve => {
          try {
            // only show published episodes
            const isPublished = !allowDraftEpisode
              ? await episodeManager.getProp(episodeId, 'isPublished')
              : false;

            if (allowDraftEpisode || isPublished) {
              let episode = episodes[episodeId];
              if (!isObject(episode)) {
                episode = await episodeManager.getById(episodeId);
              }

              return resolve({
                ...episode,
                episodeId,
              });
            }

            resolve({});
          } catch (err) {
            resolve({});
          }
        });
      })
    );

    // order by index
    const filteredEpisodes = nepisodes.filter(episode => !isEmpty(episode));
    this.setState({
      episodes: orderBy([...filteredEpisodes], ['index'], ['asc']),
    });
  }

  toggleSetupModal = () => {
    const { showModal } = this.props;
    const { episodes } = this.state;

    // must not empty episodes
    if (isEmpty(episodes)) {
      return showModal({
        content: ALERTS().EPISODE_DRIPPING.EMPTY_EPISODES,
      });
    }

    if (!canUserAccessFeature('EPISODE_DRIPPING')) {
      return showModal({
        title: 'Upgrade Required',
        content: ALERTS().EPISODE_DRIPPING.NO_ACCESS,
        upgrade: true,
      });
    }

    this.toggleModal();
  };

  toggleModal = () => {
    this.setState({
      show: !this.state.show,
    });
  };

  onEpisodeInputChange = episodeId => {
    return interval => {
      // update episode state
      const { episodes = [], updatedEpisodes: updatedIntervals } = this.state;

      // get old episode intervals
      const episodeIntervals = episodes.reduce((prev, episode) => {
        const { releaseInterval = 0 } = episode;
        prev[episode.episodeId] = releaseInterval;
        return prev;
      }, {});

      // merge updated intervals
      const intervals = {
        ...episodeIntervals,
        ...updatedIntervals,
      };

      // change interval of the updated episode
      if (episodeId in intervals) {
        intervals[episodeId] = interval;
      }

      this.setState({
        updatedEpisodes: intervals,
      });
    };
  };

  areValuesValid = () => {
    const { updatedEpisodes = {} } = this.state;
    return every(
      Object.values(updatedEpisodes).map(val => val >= 0),
      Boolean
    );
  };

  onSave = () => {
    const { episodes, updatedEpisodes: releaseIntervals } = this.state;
    const { publisherId, soundcastId } = this.props;
    if (isEmpty(releaseIntervals)) {
      return this.toggleModal();
    }

    this.setState({ updating: true }, () => {
      Axios.post(`/api/soundcast/${soundcastId}/episodes/release_interval`, {
        releaseIntervals,
        publisherId,
      })
        .then(() => {
          this.setState({ updating: false, updatedEpisodes: {} }, () => {
            // update episodes state
            Object.keys(releaseIntervals).forEach(episodeId => {
              const episodeIndex = episodes.findIndex(ep => isEqual(ep.episodeId, episodeId));
              if (episodeIndex >= 0) {
                episodes[episodeIndex].releaseInterval = releaseIntervals[episodeId];
              }
            });

            this.setState({ episodes }, () => {
              this.toggleModal();
              this.props.showModal({
                content: ALERTS().EPISODE_DRIPPING.SCHEDULED,
              });

              // call the api that updates delivery schedules
              Axios.post(`/api/soundcast/${soundcastId}/episodes/delivery_update`);
            });
          });
        })
        .catch(error => {
          console.error(error);
          this.setState({ updating: false }, () => {
            this.props.showModal({
              content: ALERTS().EPISODE_DRIPPING.SAVING_ERROR,
            });
          });
        });
    });
  };

  renderContent() {
    const { episodes = [], updatedEpisodes } = this.state;
    return episodes.map((episode, key) => {
      const { episodeId, releaseInterval: interval = 0, isPublished = false } = episode;
      // if episode interval already updated, set as default
      const intervalValue = episodeId in updatedEpisodes ? updatedEpisodes[episodeId] : interval;
      const status = isPublished ? 'PUBLISHED' : 'DRAFT';
      const statusClass = classNames('text-extra-small', {
        draft: !isPublished,
        published: isPublished,
      });

      const statusTitle = isPublished
        ? 'Published track will deliver on time'
        : "Draft track won't deliver until published";

      return (
        <DripRow key={key}>
          <EpisodeWrapper>
            <EpisodeStatus className={statusClass} title={statusTitle}>
              {status}
            </EpisodeStatus>
            <div className="text-large text-black font-weight-400">{episode.title}</div>
          </EpisodeWrapper>
          <DrippingInput value={intervalValue} onChange={this.onEpisodeInputChange(episodeId)} />
        </DripRow>
      );
    });
  }

  render() {
    const { show, updating, updatedEpisodes } = this.state;
    const accessible = canUserAccessFeature('EPISODE_DRIPPING');

    return (
      <RowDivision className="row">
        <div className="col-lg-9 col-md-9 col-sm-12 col-xs-12">
          <DivisionLabel>
            Track Dripping
            {!accessible && <HigherPlanTag className="margin-one-left" />}
            <HelperText>
              Only get part of content at designated time, delayed track access.
            </HelperText>
          </DivisionLabel>
          <SetupButton className="btn" onClick={this.toggleSetupModal}>
            Set up track dripping
          </SetupButton>
        </div>
        {accessible && (
          <ModalDialog
            title="Setup track dripping"
            open={show}
            actions={[
              <CloseButton
                className="btn text-medium margin-two-right naked"
                onClick={this.toggleModal}>
                Close
              </CloseButton>,
              <SaveButton
                className="btn text-medium"
                disabled={isEmpty(updatedEpisodes) || !this.areValuesValid() || updating}
                onClick={this.onSave}>
                {updating && <Loading align="center" type="circular" size={23} fill="#fff" />}
                {!updating && 'Save'}
              </SaveButton>,
            ]}
            bodyStyle={{ overflowY: 'auto', padding: '0px 24px 10px' }}
            actionsContainerStyle={{
              padding: '20px 24px',
              border: '1px solid #f6f6f6',
            }}>
            <React.Fragment>
              <DripRowHeader>
                <DivFlex>
                  <i className="material-icons margin-five-right">playlist_play</i>
                  <span className="text-large text-black font-weight-600">Tracks</span>
                </DivFlex>
                <DivFlex>
                  <span className="text-large text-black font-weight-600">
                    Delay{' '}
                    <span className="text-large font-weight-300">
                      day(s) from when user signs up
                    </span>
                  </span>
                </DivFlex>
              </DripRowHeader>
              <DripContent>{this.renderContent()}</DripContent>
            </React.Fragment>
          </ModalDialog>
        )}
      </RowDivision>
    );
  }
}

const DripContent = styled.div`
  ${'' /* max-height: ${(window.innerHeight ||
    document.documentElement.clientHeight ||
    document.body.clientHeight) - 335}px;
  overflow-y: scroll; */}

  div {
    &:last-child {
      margin: 0;
    }
  }
`;

const SetupButton = styled(ActionButton)`
  background-color: #369563;
  border-radius: 3px;
  font-family: 'Open Sans', sans-serif !important;
  font-size: 1.6rem;
  font-weight: normal;
`;

const DivFlex = styled.div`
  display: flex;
  align-items: center;
`;

const Row = styled(DivFlex)`
  align-items: center;
  justify-content: space-between;
  padding: 5px 10px;
`;

const DripRow = styled(Row)`
  background-color: #f4f4f4;
  margin-bottom: 5px;

  .drip-input-delay {
    width: 90px;
    font-size: 1.6rem;
    padding: 5px;
    margin: 0 0 0 10px;
    border-radius: 3px;
  }
`;

const DripRowHeader = styled(Row)`
  padding: 10px 0px;
`;

const EpisodeWrapper = styled(DivFlex)`
  flex-direction: column;
  align-items: flex-start;
`;

const EpisodeStatus = styled.div`
  padding: 0 5px;
  border-radius: 0.25em;
  font-weight: 600;
  line-height: 15px;
  border: 1px solid #777777;
  margin-top: 5px;

  &.draft {
    border-color: #777777;
    color: #777777;
  }

  &.published {
    border-color: #369563;
    color: #369563;
  }
`;

Dripping.propTypes = {
  soundcastId: PropTypes.string,
  publisherId: PropTypes.string,
  episodes: PropTypes.object,
  userInfo: PropTypes.object,
  showModal: PropTypes.func,
};

Dripping.defaultProps = {
  soundcastId: '',
  publisherId: '',
  episodes: {},
  userInfo: {},
  showModal: () => {},
};

export default Dripping;
